import {
    DocumentTextIcon,
    ClipboardDocumentIcon,
    ChevronDownIcon,
    ChevronRightIcon
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import JsonView from '@uiw/react-json-view'
import { ScreenshotAttempt } from '../../api/client'
import Button from '../Button'
import Modal from '../Modal'
import { toastSuccess } from '../Notification'
import ScreenshotStepsTimeline from './ScreenshotStepsTimeline'

// Parse log lines into JSON objects, filtering out Lambda START/END lines
const parseLogLines = (logs: string): object[] => {
    return logs
        .split('\n')
        .filter((line) => !line.startsWith('START') && !line.startsWith('END') && line.trim())
        .map((line) => {
            try {
                return JSON.parse(line)
            } catch {
                return { raw: line }
            }
        })
}

type Props = {
    open: boolean
    setOpen: (open: boolean) => void
    screenshot: ScreenshotAttempt | null
}

export default function ScreenshotDetailsModal({ open, setOpen, screenshot }: Props) {
    const [showRawLogs, setShowRawLogs] = useState(false)

    if (!screenshot) return null

    const copyToClipboard = (text: string, label: string) => {
        navigator.clipboard.writeText(text)
        toastSuccess(`${label} copied to clipboard`)
    }

    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            title={`Screenshot details (ID: ${screenshot.id})`}
            icon={<DocumentTextIcon className="h-8 w-8 text-ink-700" aria-hidden="true" />}
            size="xl"
            footer={<Button text="Close" primary={false} onClick={() => setOpen(false)} />}
        >
            <div className="overflow-y-auto max-h-[50vh] space-y-6">
                {/* Error Message Section */}
                {screenshot.errorMessage && (
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-base font-semibold text-red-700">Error Message</h3>
                            <button
                                onClick={() =>
                                    copyToClipboard(screenshot.errorMessage!, 'Error message')
                                }
                                className="text-ink-500 hover:text-ink-700"
                                title="Copy to clipboard"
                            >
                                <ClipboardDocumentIcon className="h-5 w-5" />
                            </button>
                        </div>
                        <pre className="bg-red-50 border border-red-200 p-4 rounded-md font-mono text-xs whitespace-pre-wrap break-all text-red-800">
                            {screenshot.errorMessage}
                        </pre>
                    </div>
                )}

                {/* Steps Timeline */}
                {screenshot.logs && (
                    <div>
                        <h3 className="text-base font-semibold text-ink-900 mb-3">
                            Screenshot Steps
                        </h3>
                        <ScreenshotStepsTimeline logs={screenshot.logs} />
                    </div>
                )}

                {/* Raw Logs Section (collapsible) */}
                {screenshot.logs && (
                    <div>
                        <button
                            onClick={() => setShowRawLogs(!showRawLogs)}
                            className="flex items-center gap-2 text-sm text-ink-600 hover:text-ink-900"
                        >
                            {showRawLogs ? (
                                <ChevronDownIcon className="h-4 w-4" />
                            ) : (
                                <ChevronRightIcon className="h-4 w-4" />
                            )}
                            <span className="font-medium">Raw Logs</span>
                        </button>

                        {showRawLogs && (
                            <div className="mt-2">
                                <div className="flex justify-end mb-1">
                                    <button
                                        onClick={() => copyToClipboard(screenshot.logs!, 'Logs')}
                                        className="text-ink-500 hover:text-ink-700 text-xs flex items-center gap-1"
                                        title="Copy raw logs to clipboard"
                                    >
                                        <ClipboardDocumentIcon className="h-4 w-4" />
                                        Copy
                                    </button>
                                </div>
                                <div className="bg-ink-50 border border-ink-200 rounded-md p-4 max-h-96 overflow-y-auto space-y-2">
                                    {parseLogLines(screenshot.logs).map((logEntry, index) => (
                                        <div
                                            key={index}
                                            className="border-b border-ink-100 pb-2 last:border-b-0 last:pb-0"
                                        >
                                            <JsonView
                                                value={logEntry}
                                                collapsed={1}
                                                displayDataTypes={false}
                                                style={{
                                                    fontSize: '12px',
                                                    backgroundColor: 'transparent'
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {!screenshot.errorMessage && !screenshot.logs && (
                    <div className="text-ink-500 text-center py-8">
                        No error message or logs available for this screenshot.
                    </div>
                )}
            </div>
        </Modal>
    )
}
